import { inject } from 'vue'
import type { App } from 'vue'
import { useSpace } from '@/store/modules/App/space'
import { useOrganizationStore } from '@/store/modules/Organization/index'
import { FeatureLimitsKeys } from '@/types'
import type { FeatureLimit } from '@/types'
import { FeatureLimitsTypes } from '@/types/feature-limits'
import type { FeatureLimitTerm } from '@/types/space'
import { getInternalAppBySlug } from '@/utils/helpers/internalApps'

/*
 * This helper can be used to determine if a space has access to specific features
 * based on it's plan level. It reads the space from the store
 * See: https://www.storyblok.com/pricing for more details
 */

export const SbIsFeatureAvailable = Symbol()
export const SbIsOrgFeatureAvailable = Symbol()
export const SbIsAppAvailable = Symbol()
export const SbGetFeatureLimit = Symbol()
export const SbGetOrgFeatureLimit = Symbol()
export const SbGetFeatureLimitMessage = Symbol()
export const SbTermsOfUse = Symbol()
export type IsAppAvailable = (appSlug: string) => boolean
export type IsFeatureAvailable = (feature: FeatureLimitsKeys) => boolean
export type IsOrgFeatureAvailable = (feature: FeatureLimitsKeys) => boolean
export type TermsOfUse = (
  feature: FeatureLimitsKeys,
  termsOfUseKey: string,
) => FeatureLimitTerm | undefined
export type GetOrgFeatureLimit = (feature: FeatureLimitsKeys) => number
export type GetFeatureLimit = (feature: FeatureLimitsKeys) => number
export type GetFeatureLimitMessage = (feature: FeatureLimitsKeys) => string

export type FeatureLimitMethods = {
  isFeatureAvailable: IsFeatureAvailable
  isAppAvailable: IsAppAvailable
  getTermsOfUse: TermsOfUse
  getFeatureLimit: GetFeatureLimit
  getFeatureLimitMessage: GetFeatureLimitMessage
  getOrgFeatureLimit: GetOrgFeatureLimit
  isOrgFeatureAvailable: IsOrgFeatureAvailable
}

export function isFeatureAvailable(feature: FeatureLimitsKeys): boolean {
  const featureLimits = useSpace().getFeatureLimits || {}
  const featureLimit = featureLimits[feature]

  return typeof featureLimit === 'object' && 'is_available' in featureLimit
    ? featureLimit.is_available
    : true
}

export function getFeatureLimit(feature: FeatureLimitsKeys): number {
  const spaceStore = useSpace()

  const featureLimits = spaceStore.getFeatureLimits || []

  if (featureLimits[feature]?.limit_type === FeatureLimitsTypes.Integer) {
    const value = parseInt(featureLimits[feature].limit)
    return value === -1 && feature === FeatureLimitsKeys.CustomWorkflows
      ? 999999
      : value
  }
  if (featureLimits[feature]?.limit_type === FeatureLimitsTypes.Float) {
    return parseFloat(featureLimits[feature].limit)
  }

  return 0
}

export function getTermsOfUse(
  feature: FeatureLimitsKeys,
  termsOfUseKey: string,
): FeatureLimitTerm | undefined {
  const spaceStore = useSpace()
  const featureLimits = spaceStore.getFeatureLimits || []
  const featureLimit = featureLimits[feature]

  const termOfUse = featureLimit?.terms.find(
    (term) => term.key === termsOfUseKey,
  )

  if (!termOfUse) {
    return
  }

  return termOfUse
}

export function isOrgFeatureAvailable(feature: FeatureLimitsKeys): boolean {
  const featureLimits = useOrganizationStore().orgFeatureLimits || {}
  const featureLimit = featureLimits[feature]

  return typeof featureLimit === 'object' && 'is_available' in featureLimit
    ? featureLimit.is_available
    : true
}

export function getOrgFeatureLimit(feature: FeatureLimitsKeys): number {
  const orgStore = useOrganizationStore()
  const featureLimits = orgStore.orgFeatureLimits || []

  if (featureLimits[feature]?.limit_type === FeatureLimitsTypes.Integer) {
    return featureLimits[feature].limit
      ? parseInt(featureLimits[feature].limit)
      : 0
  }
  if (featureLimits[feature]?.limit_type === FeatureLimitsTypes.Float) {
    return featureLimits[feature].limit
      ? parseFloat(featureLimits[feature].limit)
      : 0
  }

  return 0
}

export default {
  install: function (app: App): void {
    const spaceStore = useSpace()

    app.config.globalProperties.$canUseFeatureLimit = function (
      feature: FeatureLimitsKeys,
    ): boolean | number | string | undefined {
      const featureLimits = spaceStore.getFeatureLimits || {}
      const featureLimit = featureLimits[feature]

      if (!featureLimit) {
        return true
      }

      if (featureLimit.limit_type === FeatureLimitsTypes.Integer) {
        return parseInt(featureLimit.limit)
      }

      if (featureLimit.limit_type === FeatureLimitsTypes.Boolean) {
        return featureLimit.is_available
      }

      return featureLimit.limit
    }

    app.config.globalProperties.$isFeatureAvailable = isFeatureAvailable

    app.config.globalProperties.$isOrgFeatureAvailable = isOrgFeatureAvailable

    app.config.globalProperties.$getFeature = function (
      feature: FeatureLimitsKeys,
    ): FeatureLimit | undefined {
      const featureLimits = spaceStore.getFeatureLimits || []

      return featureLimits[feature]
    }

    app.config.globalProperties.$getFeatureLimit = getFeatureLimit

    app.config.globalProperties.$getOrgFeatureLimit = getOrgFeatureLimit

    app.config.globalProperties.$getFeatureLimitMessage = function (
      feature: FeatureLimitsKeys,
    ): string {
      const featureLimits = spaceStore.getFeatureLimits || {}
      const featureLimit = featureLimits[feature]

      if (!featureLimit) {
        return ''
      }

      return featureLimit.limit_type === FeatureLimitsTypes.String
        ? featureLimit.limit
        : ''
    }

    app.config.globalProperties.$isAppAvailable = function (appSlug): boolean {
      // check if external app slug exists
      const slugExistsInFeatureLimits = Object.keys(FeatureLimitsKeys).some(
        (v) => v === appSlug,
      )

      if (slugExistsInFeatureLimits) {
        // @ts-expect-error -- TODO convert FeatureLimitsKeys from enum to object to allow indexing
        return this.$isFeatureAvailable(FeatureLimitsKeys[appSlug])
      } else if (!slugExistsInFeatureLimits) {
        // check if it's an internal app slug
        const internalApp = getInternalAppBySlug(appSlug)

        if (internalApp) {
          return this.$isFeatureAvailable(internalApp.featureLimitKey)
        }
      }

      // return true for unknown app slugs
      return true
    }

    app.config.globalProperties.$getTermsOfUseVersion = function (
      feature: FeatureLimitsKeys,
      termsOfUseKey: string,
    ): string | undefined {
      const featureLimit = this.$getFeature(feature)

      const termOfUse = featureLimit?.terms.find(
        (term) => (term.key = termsOfUseKey),
      )

      if (!termOfUse) {
        return
      }

      return `${termOfUse.key}-v${termOfUse.version}`.replaceAll('_', '-')
    }

    app.config.globalProperties.$getTermsOfUse = getTermsOfUse

    app.provide(SbIsFeatureAvailable, isFeatureAvailable)
    app.provide(SbGetFeatureLimit, getFeatureLimit)
    app.provide(
      SbIsAppAvailable,
      app.config.globalProperties.$isAppAvailable.bind(
        app.config.globalProperties,
      ),
    )
    app.provide(
      SbTermsOfUse,
      app.config.globalProperties.$getTermsOfUse.bind(
        app.config.globalProperties,
      ),
    )
    app.provide(
      SbGetFeatureLimitMessage,
      app.config.globalProperties.$getFeatureLimitMessage.bind(
        app.config.globalProperties,
      ),
    )
  },
}

export function useFeatureLimit(): FeatureLimitMethods {
  return {
    isFeatureAvailable: inject(SbIsFeatureAvailable, (feature) =>
      isFeatureAvailable(feature),
    ) as IsFeatureAvailable,
    isOrgFeatureAvailable: inject(SbIsOrgFeatureAvailable, (feature) =>
      isOrgFeatureAvailable(feature),
    ) as IsOrgFeatureAvailable,
    isAppAvailable: inject(SbIsAppAvailable) as IsAppAvailable,
    getFeatureLimit: inject(SbGetFeatureLimit) as GetFeatureLimit,
    getOrgFeatureLimit: inject(SbGetOrgFeatureLimit, (value) =>
      getOrgFeatureLimit(value),
    ) as GetOrgFeatureLimit,
    getTermsOfUse: inject(SbTermsOfUse) as TermsOfUse,
    getFeatureLimitMessage: inject(
      SbGetFeatureLimitMessage,
    ) as GetFeatureLimitMessage,
  }
}
